import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SharedModule } from "../../../ui-components/shared.module";
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SpritePlayerComponent, SpriteSheet } from '../../../ui-components/sprite-player/sprite-player.component';

/**
 * Le salut de Willy. Tant que la planche dessinée n'existe pas, l'image fixe
 * de Willy qui salue, balancée ; la vraie planche se branche ici, sur cette
 * seule ligne (par exemple 12 images de 256 × 276 en 1 120 ms).
 */
export const WILLY_WAVE_SPRITE: SpriteSheet = { src: '/assets/images/willy/willy_wave.webp', frames: 12, frameWidth: 256, frameHeight: 276, durationMs: 1120 };

/** Entrée et sortie de Willy, comme la maquette (A2-willy) ; mêmes durées dans le SCSS. */
export const WILLY_GREET_IN_MS = 280;
export const WILLY_GREET_OUT_MS = 280;

/**
 * La carotte du milieu de la barre : elle ouvre Willy (appui puis saut au
 * doigt, clic ou clavier) et le referme.
 *
 * `greet` passé à vrai : Willy prend un instant la place de la carotte
 * (280 ms), salue en jouant sa planche (`WILLY_WAVE_SPRITE`), puis la carotte
 * revient (280 ms), et `greeted` l'annonce. Rien sous
 * le mouvement réduit, ni quand Willy est ouvert ou que la carotte saute. Le
 * plafond d'une fois par jour n'est pas ici : il vient de
 * `WillyGreetingService`, que l'écran consulte avant de passer `greet`.
 */
@Component({
    selector: 'vw-app-menu-central',
    imports: [
        SharedModule,
        NgClass,
        TranslatePipe,
        SpritePlayerComponent
    ],
    templateUrl: './app-menu-central.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './app-menu-central.component.scss'
})
export class AppMenuCentralComponent implements OnChanges, OnDestroy {
  @Input() isMenuOpen = false;
  /** Willy salue quand l'entrée passe à vrai. */
  @Input() greet = false;
  @Output() openAIPopup = new EventEmitter<void>();
  /** Le salut est fini (ou n'a pas eu lieu) : l'écran peut remettre `greet` à faux. */
  @Output() greeted = new EventEmitter<void>();

  @ViewChild('button', { static: true }) private button?: ElementRef<HTMLButtonElement>;

  isJumping = false;
  isPreparing = false;
  readonly wave = WILLY_WAVE_SPRITE;
  /** Où en est le salut : Willy entre, salue, puis repart ; `null` au repos. */
  greetPhase: 'in' | 'wave' | 'out' | null = null;
  private greetTimer?: ReturnType<typeof setTimeout>;

  /** Willy est à la place de la carotte. */
  get greeting(): boolean {
    return this.greetPhase !== null;
  }

  /** Image fixe en attendant la planche : un léger balancement fait le salut. */
  get stillWave(): boolean {
    return this.wave.frames <= 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['greet']?.currentValue && !changes['greet'].previousValue) {
      this.startGreeting();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.greetTimer);
  }

  /** Purement décoratif : le bouton garde son nom, « Parler à Willy ». */
  private startGreeting(): void {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || this.isMenuOpen || this.isJumping || this.greeting) {
      this.greeted.emit();
      return;
    }
    this.greetPhase = 'in';
    this.greetTimer = setTimeout(() => {
      this.greetPhase = 'wave';
      // Filet : si la fin de la planche ne vient pas (onglet caché), Willy repart quand même.
      this.greetTimer = setTimeout(() => this.onWaveEnded(), this.wave.durationMs + 200);
    }, WILLY_GREET_IN_MS);
  }

  /** La planche est jouée : Willy repart, la carotte revient. */
  onWaveEnded(): void {
    if (this.greetPhase !== 'wave') return;
    clearTimeout(this.greetTimer);
    this.greetPhase = 'out';
    this.greetTimer = setTimeout(() => this.stopGreeting(), WILLY_GREET_OUT_MS);
  }

  private stopGreeting(): void {
    if (!this.greeting) return;
    clearTimeout(this.greetTimer);
    this.greetPhase = null;
    this.greeted.emit();
  }

  /** Willy refermé : le focus revient à la carotte, qui l'a ouvert. */
  focus() {
    this.button?.nativeElement.focus({ preventScroll: true });
  }

  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  onAnimationEnd(event: AnimationEvent) {
    if (event.animationName.endsWith('carrotJump')) {
      setTimeout(() => {
        this.isJumping = false;
      }, 200);
    }
  }

  prepare() {
    // Un toucher pendant le salut : la carotte revient tout de suite et saute.
    this.stopGreeting();
    if (!this.isJumping && !this.isMenuOpen) {
      this.isPreparing = true;
    }
  }

  /**
   * Fin de l'appui au doigt. Le navigateur émet ensuite un clic émulé : on
   * l'annule, sinon `activate()` refermerait aussitôt la feuille ouverte.
   * Ouverte, la carotte la referme tout de suite, même pendant son saut
   * (1,2 s) : un toucher ignoré ferait croire qu'elle ne referme pas.
   */
  jump(event?: TouchEvent) {
    if (event?.cancelable) event.preventDefault();
    if (!this.isJumping && this.isPreparing && !this.isMenuOpen) {
      this.isPreparing = false;
      this.isJumping = true;
      this.openAIPopup.emit();
    } else if (this.isMenuOpen && !this.isPreparing) {
      this.openAIPopup.emit();
    }
  }

  /** Souris et clavier : pas d'appui à préparer, le saut part tout de suite. */
  activate() {
    this.prepare();
    this.jump();
  }
}
